// Agent connections (spec §10): a token per agent, shown once, revocable; plus how to connect Codex.
import { useEffect, useState, type FormEvent } from 'react';
import { api, type AgentToken, type Me } from '../api.js';
import { href } from '../router.js';

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** Copies `text`; true on success. The caller shows its own feedback either way — the Clipboard
 * API can be unavailable (an insecure context, denied permission, an older browser). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function Settings({ me }: { me: Me }) {
  const [tokens, setTokens] = useState<AgentToken[]>([]);
  const [mcpUrl, setMcpUrl] = useState(`${location.origin}/mcp`);
  const [label, setLabel] = useState('Codex');
  const [created, setCreated] = useState<{ id: string; label: string; token: string } | null>(null);
  const [error, setError] = useState('');
  // No browser confirm()/alert dialogs (review item 8): Revoke asks in-page, and Copy reports
  // success or failure inline rather than silently doing nothing on an older browser.
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<Record<string, string>>({});
  const copy = async (key: string, text: string) => {
    const ok = await copyText(text);
    setCopyStatus((s) => ({ ...s, [key]: ok ? 'Copied.' : "Couldn't copy automatically — select and copy it yourself." }));
  };

  const load = () =>
    api<{ tokens: AgentToken[]; mcpUrl: string }>('GET', '/api/tokens').then(
      (r) => {
        setTokens(r.tokens);
        setMcpUrl(r.mcpUrl);
      },
      (e) => setError((e as Error).message),
    );
  useEffect(() => {
    void load();
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setCreated(await api<{ id: string; label: string; token: string }>('POST', '/api/tokens', { label }));
      void load();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const revoke = async (id: string) => {
    setError('');
    setConfirmRevoke(null);
    try {
      await api('DELETE', `/api/tokens/${id}`);
      if (created?.id === id) setCreated(null);
      void load();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const codexSnippet = created ? `[mcp_servers.plana]\nurl = "${mcpUrl}"\nhttp_headers = { "Authorization" = "Bearer ${created.token}" }` : '';

  return (
    <main className="settings">
      <header className="topbar">
        <a href={href.projects()} className="back">← Projects</a>
        <h1>Settings</h1>
        <span className="spacer" />
        <span className="who">{me.name}</span>
      </header>
      <section className="panel">
        <h2>Agent connections</h2>
        <p className="muted">
          An agent token lets your own agent (Codex, the ChatGPT app, Claude) read your projects and add research through PlanA's MCP server. It acts for
          you and reaches only projects you're a member of. It can't accept findings, delete anything, or change members or tokens. Revoke it at any time.
        </p>
        <form className="token-form" onSubmit={create}>
          <label>
            Name
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} required />
          </label>
          <button type="submit">Create token</button>
        </form>
        {created && (
          <div className="token-created">
            <p>
              <strong>Copy this token now.</strong> It is shown only once.
            </p>
            <p className="copy-row">
              <code className="secret" data-testid="new-token">{created.token}</code>
              <button type="button" className="link small" onClick={() => void copy('secret', created.token)}>Copy</button>
              {copyStatus.secret && <span role="status" className="muted small">{copyStatus.secret}</span>}
            </p>
            <p className="muted small">Codex (the CLI and the ChatGPT app): add this to ~/.codex/config.toml, then quit and reopen the app.</p>
            <pre className="code">{codexSnippet}</pre>
            <p className="copy-row">
              <button type="button" className="link small" onClick={() => void copy('codex', codexSnippet)}>Copy</button>
              {copyStatus.codex && <span role="status" className="muted small">{copyStatus.codex}</span>}
            </p>
            <p className="muted small">Claude Code:</p>
            <pre className="code">{`claude mcp add --transport http plana ${mcpUrl} \\\n  --header "Authorization: Bearer ${created.token}"`}</pre>
            <p className="muted small">Other MCP clients: Streamable HTTP at {mcpUrl} with the header Authorization: Bearer &lt;token&gt;.</p>
            <button className="link" onClick={() => setCreated(null)}>I've copied it</button>
          </div>
        )}
        {error && <p className="error" role="alert">{error}</p>}
        <ul className="token-list">
          {tokens.map((t) => (
            <li key={t.id} className={t.revokedAt ? 'revoked' : ''}>
              <strong>{t.label}</strong> <code>{t.prefix}…</code>
              <small className="muted">
                {' '}Created {when(t.createdAt)}
                {t.lastUsedAt ? ` · last used ${when(t.lastUsedAt)}` : ' · never used'}
                {t.revokedAt ? ` · revoked ${when(t.revokedAt)}` : ''}
              </small>
              {!t.revokedAt &&
                (confirmRevoke === t.id ? (
                  <span className="danger-row">
                    <span className="muted small">Revoke this token?</span>
                    <button className="link small danger" onClick={() => void revoke(t.id)}>Yes, revoke</button>
                    <button className="link small" onClick={() => setConfirmRevoke(null)}>Cancel</button>
                  </span>
                ) : (
                  <button className="link small danger" onClick={() => setConfirmRevoke(t.id)}>Revoke</button>
                ))}
            </li>
          ))}
          {tokens.length === 0 && <li className="muted">No tokens yet.</li>}
        </ul>
        <p className="muted small">
          MCP address: <code>{mcpUrl}</code>. Connected agents read the full guide with the <code>get_workflow_guide</code> tool.
        </p>
      </section>
    </main>
  );
}
